/**
 * The offline half of the curtain (#43, US-6.1 AC2): what the guide knew in
 * advance — the Lower Park's hours, closures, the nearest toilets and food by
 * straight line, the comfort reference — each with the date of its snapshot.
 *
 * It renders inside the existing «Гид временно недоступен» curtain, so the
 * layout of the redesign is unchanged; the position comes from the browser's GPS
 * on an explicit tap (GPS works without a network) and is never stored.
 */

import { useState } from 'react';

import { OFFLINE, closuresOn, comfortOn, hoursOn, museumDay, nearest, ruDate, type Near } from './data';

const STATUS: Record<string, string> = { closed_renovation: 'закрыт на реставрацию', closed: 'закрыт' };

type Spot = { toilets: Near[]; food: Near[] } | { error: string } | null;

function NearList({ title, rows }: { title: string; rows: Near[] }) {
  return (
    <div className="offline__near">
      <h4 className="offline__subtitle">{title}</h4>
      {rows.length ? (
        <ul>
          {rows.map((row) => (
            <li key={row.place.id}>
              {row.place.title} — около {row.metres} м по прямой, {row.bearing}
            </li>
          ))}
        </ul>
      ) : (
        <p className="offline__muted">В пределах 2 км по каталогу музея нет.</p>
      )}
    </div>
  );
}

export default function OfflineGuide({ now = new Date() }: { now?: Date }) {
  const day = museumDay(now);
  const [spot, setSpot] = useState<Spot>(null);
  const hours = hoursOn(day);
  const closures = closuresOn(day);
  const comfort = comfortOn(day);

  const locate = () => {
    if (!('geolocation' in navigator)) {
      setSpot({ error: 'Геопозиция в этом браузере недоступна.' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const here = { lat: coords.latitude, lon: coords.longitude };
        setSpot({ toilets: nearest(OFFLINE.catalogue.toilets, here), food: nearest(OFFLINE.catalogue.food, here) });
      },
      () => setSpot({ error: 'Не удалось определить положение — разрешите геопозицию и попробуйте ещё раз.' }),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
    );
  };

  return (
    <section className="offline" aria-label="Без сети">
      <h3 className="offline__title">Без сети — то, что гид знает заранее</h3>

      <div className="offline__block">
        <h4 className="offline__subtitle">Туалеты и еда рядом</h4>
        <button type="button" className="btn" onClick={locate}>
          Найти по геопозиции
        </button>
        {spot && 'error' in spot && <p className="offline__muted">{spot.error}</p>}
        {spot && 'toilets' in spot && (
          <>
            <NearList title="Туалеты" rows={spot.toilets} />
            <NearList title="Кафе, киоски, автоматы" rows={spot.food} />
          </>
        )}
        <p className="offline__muted">
          Расстояние по прямой, не путь. Места — по карте музея на {ruDate(OFFLINE.catalogue.as_of)}; работает ли
          кафе сейчас, уточняйте на месте.
        </p>
      </div>

      <div className="offline__block">
        <h4 className="offline__subtitle">Часы Нижнего парка</h4>
        {hours.length ? (
          hours.map((row) => (
            <p key={row.from}>
              {row.text} <span className="offline__muted">По данным на {ruDate(row.as_of)}.</span>
            </p>
          ))
        ) : (
          <p className="offline__muted">На сегодня в снимке часов нет — уточняйте на месте.</p>
        )}
      </div>

      {closures.length > 0 && (
        <div className="offline__block">
          <h4 className="offline__subtitle">Закрыто</h4>
          <ul>
            {closures.map((row) => (
              <li key={row.title}>
                {row.title} — {STATUS[row.status] ?? 'закрыт'}{' '}
                <span className="offline__muted">(проверено {ruDate(row.checked)})</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <details className="offline__block">
        <summary className="offline__subtitle">Услуги и удобства</summary>
        <ul>
          {comfort.map((row) => (
            <li key={row.text}>{row.text}</li>
          ))}
        </ul>
        <p className="offline__muted">По сайту музея на {ruDate(OFFLINE.comfort.as_of)}.</p>
      </details>

      <p className="offline__muted">Это снимок данных гида, а не живые данные музея на сегодня.</p>
    </section>
  );
}
